import { UndefinedOption } from '@df/ui/atomic/atomic-options';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { PAGE_HEADER_VERSION_CONFIG } from '../../../../../layout/page/header/page-header.config';
import { EMediaRowLayout } from './product-view-layout.interface';

export const PRODUCT_VIEW_LAYOUT_CONFIG: Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'> = {
    id: 'product-page-layout',
    onDemandOnly: true,
    block: {
        url: 'product-page-layout'
    },
    name: 'Product page layout',
    description: 'Product page layout block',
    fields: [
        // Media gallery (top section)
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-information',
            title: 'Product information'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'priceNote',
            title: 'Price note',
            description: 'Price note appears next to the product price'
        },
        {
            key: 'delivery_info',
            type: NICmsContentField.EFieldType.text,
            title: 'Delivery info',
            description: 'Customize delivery info'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-gallery',
            title: 'Top media gallery'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'requiredMediaLabels',
            title: 'Whitelisted media roles',
            description: 'Comma separated. If set then only media items with these labels/roles will be shown'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'bannedMediaLabels',
            title: 'Blacklisted media roles',
            description: 'Comma separated. If set media items with these labels/roles will be skipped'
        },
        // Media gallery rows
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'galleryRows',
            title: 'Media gallery rows',
            itemName: 'Row',
            itemNameField: 'row',
            defaultSize: 2,
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'layout',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Layout',
                    options: [
                        { key: EMediaRowLayout.portraitsPair, title: 'Two portraits' },
                        { key: EMediaRowLayout.landscape, title: 'Single landscape' }
                    ],
                    description: 'Media display layout',
                    default: EMediaRowLayout.portraitsPair
                }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'usp_block_variant',
            type: NICmsContentField.EFieldType.choice,
            title: 'USP Block Variant',
            options: [
                ...UndefinedOption,
                { key: 'Furniture', title: 'Furniture' },
                { key: 'Fashion', title: 'Fashion' },
                { key: 'Accessories', title: 'Accessories' }
            ]
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'detailsAttributes',
            title: 'Product details',
            itemName: 'section',
            itemNameField: 'heading',
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    title: 'Heading',
                    key: 'heading',
                    default: 'Lorem ipsum'
                },
                <NICmsContentField.IToggleField>{
                    key: 'bulletpoints',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Prepend bulletpoint',
                    default: false,
                    labels: {
                        true: 'Yes',
                        false: 'No'
                    }
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'attributes',
                    title: 'Attributes',
                    itemName: 'Attribute',
                    itemNameField: 'code',
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Type',
                            key: 'type',
                            options: [
                                { key: 'attribute', title: 'Select Attribute' },
                                { key: 'property', title: 'Attribute Code' }
                            ],
                            default: 'attribute'
                        },
                        <NICmsContentField.IRelationField>{
                            key: 'code',
                            type: NICmsContentField.EFieldType.relation,
                            title: 'Select Attribute',
                            options: {
                                title: 'Attribute',
                                type: 'attribute',
                                qty: 1
                            },
                            canShow: data => data.type === 'attribute'
                        },
                        {
                            key: 'code_manual',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Attribute Code',
                            canShow: data => data.type === 'property'
                        },
                        <NICmsContentField.IChoiceField>{
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Display mode',
                            key: 'mode',
                            options: [
                                { key: 'attributeValue', title: 'Attribute values only (Blue)' },
                                { key: 'attributeTitleValue', title: 'Attribute labels and value (Color: Blue)' },
                                { key: 'cms', title: 'CMS block' }
                            ],
                            default: 'attribute'
                        }
                    ]
                }
            ]
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-information',
            title: 'Return information'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'returnNote',
            title: 'Return note'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'returnLinkText',
            title: 'Return link text'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'returnLink',
            title: 'Return link'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'gift_boxing',
            title: 'Gift boxing',
            itemName: 'copy',
            itemNameField: 'copy',
            fields: [
                {
                    type: NICmsContentField.EFieldType.textarea,
                    key: 'gift_copy',
                    title: 'Gift boxing copy'
                }
            ]
        },
        {
            type: NICmsContentField.EFieldType.image,
            key: 'gift_boxing_image',
            title: 'Gift Boxing Image',
            default: ''
        },
        <NICmsContentField.IComponentsField>{
            key: 'bottomComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Bottom components',
            itemName: 'component',
            default: []
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.label,
            key: 'label-detail-carousel',
            title: 'Detail Carousel'
        },
        <NICmsContentField.IToggleField>{
            type: NICmsContentField.EFieldType.toggle,
            key: 'detail_carousel_enabled',
            title: 'Enabled',
            labels: {
                true: 'Yes',
                false: 'No'
            },
            default: false
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'detail_carousel_title',
            title: 'It’s in the detail'
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'detail_carousel_labels',
            title: 'Carousel Media Roles',
            description: 'Comma separated. If set then only media items with these labels/roles will be shown'
        },
        ...PAGE_HEADER_VERSION_CONFIG.filter(field => field.key !== 'headerHideUp'),
        <NICmsContentField.IField>{
            key: 'other',
            type: NICmsContentField.EFieldType.label,
            title: 'Other'
        },
        <NICmsContentField.IToggleField>{
            key: 'allow_financing_tray',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Allow Financing Tray',
            default: false,
            labels: {
                true: 'Yes',
                false: 'No'
            }
        }
    ]
};
